<template>
  <b-modal ref="modal" :title="text.title" size="xl" scrollable ok-only :ok-title="text.close" ok-variant="secondary">
    <div class="sc-guide__intro">
      <p class="sc-guide__purpose">{{ text.purpose }}</p>
      <div class="sc-guide__lang">
        <button
          v-for="option in languages"
          :key="option.value"
          type="button"
          class="btn btn-sm"
          :class="lang === option.value ? 'btn-mantraz' : 'btn-outline-secondary'"
          @click="setLang(option.value)"
        >
          {{ option.label }}
        </button>
      </div>
    </div>

    <section class="sc-guide__section">
      <h6 class="sc-guide__heading">{{ text.sidesTitle }}</h6>
      <div class="sc-guide__cards">
        <div v-for="(side, i) in text.sides" :key="side.title" class="sc-guide__card">
          <i class="sc-guide__icon" :class="sideIcons[i]"></i>
          <div class="sc-guide__card-title">{{ side.title }}</div>
          <div class="sc-guide__card-body">{{ side.body }}</div>
        </div>
      </div>
      <figure v-if="text.figures.sides" class="sc-guide__figure">
        <img :src="`${imgBase}img/summary-compare/guide-sides.jpeg`" :alt="text.figures.sides.alt" loading="lazy">
        <figcaption>{{ text.figures.sides.caption }}</figcaption>
      </figure>
    </section>

    <section class="sc-guide__section">
      <h6 class="sc-guide__heading">{{ text.notesTitle }}</h6>
      <ul class="sc-guide__notes">
        <li v-for="(note, i) in text.notes" :key="note.title">
          <i class="sc-guide__note-icon" :class="noteIcons[i]"></i>
          <span><strong>{{ note.title }}</strong> — {{ note.body }}</span>
        </li>
      </ul>
      <figure v-if="text.figures.periodLength" class="sc-guide__figure">
        <img :src="`${imgBase}img/summary-compare/guide-length.jpeg`" :alt="text.figures.periodLength.alt" loading="lazy">
        <figcaption>{{ text.figures.periodLength.caption }}</figcaption>
      </figure>
    </section>
  </b-modal>
</template>

<script>
const LANG_KEY = 'summaryCompareGuideLang';

const GUIDE = {
  en: {
    title: 'How to use Summary Compare',
    close: 'Close',
    purpose: 'Compare summary numbers for your Base period (column A) against comparison columns (B, C, …) — as many as fit on your screen, up to 9 — another vessel, another period, or custom dates. This tab shows numbers, not charts — for charts, use Custom Chart instead.',
    sidesTitle: 'The columns',
    sides: [
      { title: 'Base (A)', body: 'The vessel and date range you picked in the filter at the top of the page. Every delta is calculated against this column.' },
      { title: 'Comparison columns (B, C, …)', body: 'Set in the "Compare with" bar — add as many as fit on your screen (up to 9). Pick Same vessel + Previous period to compare periods of the same vessel, pick another vessel to compare vessels, or pick Custom dates for your own range.' },
    ],
    notesTitle: 'Reading the numbers',
    notes: [
      { title: 'Delta', body: 'Each comparison column as a percentage of Base (A). Its color is neutral gray on purpose: fuel going up is bad but distance going up is good, so coloring it by sign would be misleading.' },
      { title: 'Different period length', body: 'When a column doesn\'t cover the same number of days as Base, its total rows (fuel, distance, volume, running time) get a "!" mark for that column because the totals aren\'t comparable. Average rows (speed, RPM, flow rate, density, temperature) stay comparable either way.' },
      { title: 'The "—" mark', body: 'Means one of two things: there is no data for that period, or that sensor type is not served by the summary endpoint yet.' },
      { title: 'Share', body: 'Every choice you make lives in the page URL, so you can send the link to someone else and it opens with the same comparison.' },
    ],
    figures: {
      sides: {
        alt: 'Summary Compare cards showing the Base column, comparison columns and delta for GPS and Main engine data.',
        caption: 'Base (A) comes from the filter at the top; comparison columns (B, C, …) are set in the Compare with bar.',
      },
      periodLength: {
        alt: 'A warning note above the cards and total rows marked with an exclamation mark because a comparison column differs in length from Base.',
        caption: 'When a column covers a different length than Base, a note appears above the cards and its total rows are marked.',
      },
    },
  },
  id: {
    title: 'Cara pakai Summary Compare',
    close: 'Tutup',
    purpose: 'Bandingkan angka ringkasan periode Base (kolom A) dengan kolom pembanding (B, C, …) — sebanyak yang muat di layar, maksimal 9 — kapal lain, periode lain, atau tanggal bebas. Tab ini menampilkan angka, bukan grafik — untuk grafik, pakai Custom Chart.',
    sidesTitle: 'Kolom-kolomnya',
    sides: [
      { title: 'Base (A)', body: 'Kapal dan rentang tanggal yang dipilih di filter atas halaman. Semua delta dihitung terhadap kolom ini.' },
      { title: 'Kolom pembanding (B, C, …)', body: 'Diatur di bar "Compare with" — bisa tambah sebanyak yang muat di layar (maksimal 9). Pilih Same vessel + Previous period untuk membandingkan periode kapal yang sama, pilih kapal lain untuk membandingkan kapal, atau pilih Custom dates untuk tanggal sendiri.' },
    ],
    notesTitle: 'Membaca angkanya',
    notes: [
      { title: 'Delta', body: 'Persentase tiap kolom pembanding terhadap Base (A). Warnanya sengaja netral abu-abu: fuel naik itu buruk, tapi jarak naik itu bagus, jadi mewarnai berdasarkan tanda malah menyesatkan.' },
      { title: 'Panjang periode beda', body: 'Kalau sebuah kolom nggak sama panjangnya dengan Base, baris total (fuel, jarak, volume, running time) di kolom itu dapat tanda "!" karena totalnya nggak sebanding. Baris rata-rata (speed, RPM, flow rate, density, suhu) tetap sebanding.' },
      { title: 'Tanda "—"', body: 'Punya dua arti: nggak ada data di periode itu, atau tipe sensornya belum dilayani endpoint ringkasan.' },
      { title: 'Share', body: 'Semua pilihan kamu tersimpan di URL halaman, jadi link-nya bisa dikirim ke orang lain dan terbuka dengan perbandingan yang sama.' },
    ],
    figures: {
      sides: {
        alt: 'Kartu Summary Compare menampilkan kolom Base, kolom pembanding, dan delta untuk data GPS dan Main engine.',
        caption: 'Base (A) diambil dari filter di atas; kolom pembanding (B, C, …) diatur di bar Compare with.',
      },
      periodLength: {
        alt: 'Catatan peringatan di atas kartu dan baris total bertanda seru karena kolom pembanding berbeda panjang dari Base.',
        caption: 'Kalau panjang sebuah kolom beda dari Base, ada catatan di atas kartu dan baris total kolom itu diberi tanda.',
      },
    },
  },
};

function readLang() {
  try {
    const saved = localStorage.getItem(LANG_KEY);
    return GUIDE[saved] ? saved : 'en';
  } catch (e) {
    return 'en';
  }
}

export default {
  name: 'SummaryCompareGuideModal',
  data() {
    return {
      lang: readLang(),
      languages: [
        { value: 'en', label: 'English' },
        { value: 'id', label: 'Bahasa Indonesia' },
      ],
      imgBase: process.env.BASE_URL,
      sideIcons: ['ri-arrow-left-line', 'ri-arrow-right-line'],
      noteIcons: ['ri-percent-line', 'ri-error-warning-line', 'ri-subtract-line', 'ri-share-line'],
    };
  },
  computed: {
    text() {
      return GUIDE[this.lang] || GUIDE.en;
    },
  },
  methods: {
    open() {
      this.$refs.modal.show();
    },
    setLang(lang) {
      this.lang = lang;
      try {
        localStorage.setItem(LANG_KEY, lang);
      } catch (e) {
        // Browser blok storage: pilihan cuma berlaku sesi ini.
      }
    },
  },
};
</script>

<style scoped>
.sc-guide__intro { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; }
.sc-guide__purpose { margin: 0; font-size: var(--mz-fs-md); color: var(--mz-text-strong); }
.sc-guide__lang { display: flex; gap: 6px; flex-shrink: 0; }
.sc-guide__section { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--mz-border-soft); }
.sc-guide__heading { margin-bottom: 10px; font-size: var(--mz-fs-sm); font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; color: var(--mz-text-subtle); }
.sc-guide__cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.sc-guide__card { padding: 12px; border: 1px solid var(--mz-border); border-radius: var(--mz-radius-sm); background: var(--mz-surface-2); }
.sc-guide__icon { font-size: var(--mz-fs-lg); color: var(--mz-primary-ink); }
.sc-guide__card-title { margin: 6px 0 4px; font-size: var(--mz-fs-base); font-weight: 700; color: var(--mz-text-emphasis); }
.sc-guide__card-body { font-size: var(--mz-fs-sm); color: var(--mz-text); }
.sc-guide__notes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 20px; margin: 0; padding: 0; list-style: none; }
.sc-guide__notes li { display: flex; gap: 8px; font-size: var(--mz-fs-sm); color: var(--mz-text-strong); align-items: flex-start; }
.sc-guide__note-icon { font-size: var(--mz-fs-md); color: var(--mz-text-subtle); margin-top: 1px; flex-shrink: 0; }
.sc-guide__figure { margin: 14px 0 0; text-align: center; }
.sc-guide__figure img { max-width: 100%; height: auto; max-height: 340px; object-fit: contain; border: 1px solid var(--mz-border); border-radius: 6px; }
.sc-guide__figure figcaption { margin-top: 4px; font-size: var(--mz-fs-sm); color: var(--mz-text-subtle); }

/* Layar sempit: semua jadi satu kolom. */
@media (max-width: 991px) {
  .sc-guide__intro { flex-direction: column; }
  .sc-guide__cards,
  .sc-guide__notes { grid-template-columns: minmax(0, 1fr); }
}
</style>
